import { useState } from "react";
import { api } from "../../api/client";
import { useNotify } from "../../components/notifications";
import { ListActionChoiceDialog } from "./ListActionChoiceDialog";
import type { CrudListAction } from "./moduleCrud";

/**
 * "Close Prospects" button for the Open / My Open Prospects DETAIL view, rendered BEFORE Edit in
 * the view-mode action bar via SettingsDetailPage's `recordActionsSlotBeforeEdit` (owner report
 * 2026-10-28, image 1043). It opens the SAME shared close popup the list button uses
 * (ListActionChoiceDialog — Status / Actual Revenue / Close Date, driven by {@link action}) and
 * POSTs the single record's id to the list `close` endpoint, then returns to the list (the now-closed
 * prospect drops out of the Open list). Reuses the list's close `action` config so the two paths can
 * never drift — nothing re-implemented (rule 8).
 */
export function ProspectCloseButton({ basePath, id, action, onClosed }: {
  basePath: string;
  id: string;
  action: CrudListAction;
  onClosed: () => void;
}) {
  const notify = useNotify();
  const [open, setOpen] = useState(false);
  const run = async (value: string, extras: Record<string, string>) => {
    try {
      await api.post(`${basePath}/${action.path}`, { ids: [id], [action.choiceKey ?? "status"]: value, ...extras });
      notify.success(action.successMessage);
      setOpen(false);
      onClosed();
    } catch (e) {
      const msg = (e as { response?: { data?: { error?: { message?: string } } } })?.response?.data?.error?.message;
      notify.error(msg ?? `${action.label} failed.`);
    }
  };
  return (
    <>
      <button className="sd-abtn" onClick={() => setOpen(true)} title={action.label}>
        <span className="tb-i">{action.icon ?? "🏁"}</span> {action.label}
      </button>
      {open && (
        <ListActionChoiceDialog action={action} onConfirm={run} onClose={() => setOpen(false)} />
      )}
    </>
  );
}
